import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, ReminderListItem, ReminderTriggerValue, ReminderResetDaily } from '@key-telematics/fleet-api-client';
import { AdminEditorService, TimeZoneEntry } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, NumberField, DateTimeField, CheckListField, TextField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { DAYS_OF_THE_WEEK, MONTHS_OF_THE_YEAR } from '../scheduledreport/utils';
import { set, merge, get } from 'lodash';
import * as Bluebird from 'bluebird';
import * as moment from 'moment-timezone';

type ReminderType = 'odo' | 'hours' | 'time';
type ReminderMode = 'once' | 'repeating';

interface SectionValues {
    reminders: ListFieldValue<ReminderListItem>[];
}

interface ModalValues {
    name: string;
    mode: ReminderMode;
    timeZoneId?: string;
    trigger?: ReminderTriggerValue;
    reset?: {
        type?: 'none' | 'daily' | 'weekly' | 'monthly';
        counter?: string;
        daily?: ReminderResetDaily;
        weekly?: {
            days: string[];
        };
        monthly?: {
            months: string[];
            day: number;
        }
    };
}

const NEW_ID = '::new::';

const LIST_ICONS = {
    odo: 'tachometer-slow',
    hours: 'clock',
    time: 'calendar',
};

export class AssetRemindersSectionHandler implements FormSectionHandler<AssetResponse, SectionValues> {

    type = 'form';
    icon = 'alarm-exclamation';
    bulk = true;
    modifiedReminders: { id: string, type: 'deleted' | 'edited' | 'created' }[] = [];

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.REMINDERS.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: ReminderListItem[]): Bluebird<AssetResponse> {
        return Bluebird.map(this.modifiedReminders, async item => {
            const reminder = payload.find(x => x.id === item.id);
            await Bluebird.delay(500);

            switch (item.type) {
                case 'deleted':
                    return this.admin.applyUpdate(api => api.entities.deleteReminder(item.id));
                case 'created':
                    return this.admin.applyUpdate(api => api.entities.createReminder({
                        name: reminder.name,
                        target: {
                            type: 'asset',
                            id: entity.id,
                            name: entity.name,
                        },
                        type: reminder.type,
                        mode: reminder.mode,
                        timeZoneId: reminder.timeZoneId,
                        trigger: reminder.trigger,
                        reset: reminder.reset,
                        ownerId: entity.owner.id,
                        enabled: reminder.mode !== 'once' || moment.utc(reminder.trigger.value).isValid() ? moment.utc().isAfter(moment.utc(reminder.trigger.value)) : true, // only false when mode === 'once' and trigger.value is in the past. If trigger value is not a date we make true as there is no way to tell
                    }));
                case 'edited':
                default:
                    return this.admin.applyUpdate(api => api.entities.updateReminder(reminder.id, reminder));
            }
        }, { concurrency: 1 }).then(() => {
            return entity;
        }).catch(err => {
            return Bluebird.reject(err);
        });
    }

    mapEntityToEditor(entity: AssetResponse): Promise<SectionValues> {
        this.modifiedReminders = [];
        return this.admin.getCached(`getRemindersByAsset/${entity.owner.id}/${entity.id}`, api => api.entities.listReminders(entity.owner.id, entity.id), null, true).then(res => {
            res.items.map(item => {
                if (item.type === 'odo' && item.trigger) {
                    item.trigger.value = item.trigger.value && this.admin.units.fromBackend('distance', Number(item.trigger.value), 0).toString();
                    if (!moment.utc(item.trigger.start, moment.ISO_8601, true).isValid()) {
                        item.trigger.start = item.trigger.start && this.admin.units.fromBackend('distance', Number(item.trigger.start), 0).toString();
                    }
                }
            });
            return { reminders: (res.items || []).sort((a, b) => a.name.localeCompare(b.name)).map(this.reminderToListItem) };
        });
    }

    mapEditorToEntity(_entity: AssetUpdateRequest, values: SectionValues): Promise<ReminderListItem[]> {
        this.modifiedReminders.forEach(mr => {
            const reminder = values.reminders.find(r => r.id === mr.id);
            if (reminder && reminder.data.type === 'odo' && reminder.data.trigger) {
                reminder.data.trigger.value = reminder.data.trigger.value && this.admin.units.toBackend('distance', Number(reminder.data.trigger.value), 4).toString();
                if (!moment.utc(reminder.data.trigger.start, moment.ISO_8601, true).isValid()) {
                    reminder.data.trigger.start = reminder.data.trigger.start && this.admin.units.toBackend('distance', Number(reminder.data.trigger.start), 4).toString();
                }
            }
        });
        return Promise.resolve((values.reminders || []).map(x => x.data));
    }

    reminderToListItem(reminder: ReminderListItem): ListFieldValue<ReminderListItem> {
        return {
            id: reminder.id,
            name: reminder.name,
            icon: LIST_ICONS[reminder.type],
            data: reminder,
        };
    }

    listItemToModalValues(item: ListFieldValue<ReminderListItem>): ModalValues {
        const { type, mode, trigger, reset, timeZoneId } = item.data;
        const { weekly, monthly, counter } = reset || {} as any;
        const getTriggerStart = (): string => { // remove offset to daily trigger.start so that we display the correct date when datetime picker formats it
            const offset = new Date().getTimezoneOffset();
            if (reset.type === 'daily' && type !== 'time') {
                return moment(trigger.start).add(offset, 'minutes').toISOString();
            } else {
                return trigger.start;
            }
        };
        return {
            name: item.name,
            mode,
            trigger: merge(trigger, {
                start: getTriggerStart(),
            }),
            reset: merge(reset, {
                counter: counter.toString(),
                weekly: {
                    days: weekly && weekly.days.map(x => x.toString()),
                },
                monthly: {
                    months: monthly.months.map(x => x.toString()),
                },
            }),
            timeZoneId,
        };
    }

    getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        const remindersTree = [
            { id: 'odo', name: this.translate('ODO.TITLE') },
            { id: 'hours', name: this.translate('HOURS.TITLE') },
            { id: 'time', name: this.translate('TIME.TITLE') },
        ];

        return Promise.resolve([
            {
                id: 'reminders', title: this.translate('FIELDS.REMINDERS'), type: 'list', required: false,
                actions: [
                    { id: 'delete', click: this.markDeletedItem.bind(this) },
                    { id: 'edit', click: this.showReminderModal.bind(this) },
                    { name: this.admin.i18n.instant('SHARED.ADD'), dropdownValues: remindersTree, click: this.showReminderModal.bind(this) },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, ['reminders'], fields));
    }

    async markDeletedItem(_action: ReminderType, _field: FormBuilderField, _values: SectionValues, item?: ListFieldValue<ReminderListItem>): Promise<boolean> {
        this.modifiedReminders.push({ id: item.id, type: 'deleted' });
        return true;
    }

    async showReminderModal(action: ReminderType, _field: FormBuilderField, values: SectionValues, item?: ListFieldValue<ReminderListItem>): Promise<SectionValues> {

        const modalValues: ModalValues = item ? this.listItemToModalValues(item) : {
            // add sensible defaults for new items
            name: this.translate(`${action.toUpperCase()}.TITLE`),
            mode: 'once',
            reset: {
                type: 'none',
                counter: '1',
                daily: { recurrence: 1 },
                weekly: {
                    days: [...Array(7).keys()].map(x => x.toString()),
                },
                monthly: {
                    months: [...Array(12).keys()].map(x => (++x).toString()), // index starts at 1
                    day: 1,
                },
            },
            timeZoneId: 'Africa/Johannesburg',
        };

        const type = item ? item.data.type : action;
        const timeZones = await this.admin.getTimeZones();
        const result = await this.admin.showFormModal({
            groups: [{
                name: this.translate(`${type.toUpperCase()}.TITLE`),
                description: this.translate(`${type.toUpperCase()}.DESC`),
                fields: [
                    TextField('name', this.translate('FIELDS.NAME'), true, null, null),
                    ...this.getTypeSpecificFields(type, timeZones),
                ],
            }],
        }, modalValues);


        if (result) {
            // the api requires us to send back number[] | number in cases where form fields need it to be a string[] | string
            // so converting neccessary items back to numbers
            const convertToNumber = (val: any) => val && (Array.isArray(val) ? val.map(x => parseInt(x, 0)) : parseInt(val, 0));
            ['reset.counter', 'reset.weekly.days', 'reset.monthly.months'].forEach(key => {
                set(result, key, convertToNumber(get(result, key, null)));
            });

            // add offset back that we removed for display purposes
            const { trigger, reset } = result;
            if (trigger && trigger.start && reset.type === 'daily' && type !== 'time') {
                const offset = new Date().getTimezoneOffset();
                trigger.start = moment(trigger.start).subtract(offset, 'minutes').toISOString();
            }

            if (item) {
                const index = values.reminders.findIndex(x => x.id === item.id);
                const reminder = { ...item.data, ...result };
                values.reminders[index] = this.reminderToListItem(reminder as ReminderListItem);
                this.modifiedReminders.push({ id: reminder.id, type: 'edited' });
            } else {
                const reminder = merge({
                    id: NEW_ID + new Date().getTime(),
                    owner: null,
                    modifiedDate: null,
                    type: action,
                }, result);
                values.reminders.push(this.reminderToListItem(reminder as any));
                this.modifiedReminders.push({ id: reminder.id, type: 'created' });
            }
            values.reminders.sort((a, b) => a.name.localeCompare(b.name));
            return values;
        }
        return null;

    }

    getTypeSpecificFields(type: ReminderType, timeZones: TimeZoneEntry[]): FormBuilderField[] {
        const unit = type === 'odo' && this.admin.units.unit('distance'); // currently only type odo has a unit, but if this change we can flesh this out a bit
        const key = type.toUpperCase();
        const resetCounter = Array(8).fill(type + '_cnt_').map((x, i) => ({ key: (++i).toString(), value: x + i })); // array from 1...9 of type FormBuilderKeyValue with key/value being { key: 1, value: 'odo_cnt_1' }, { key: 2, value: 'odo_cnt_2' }, etc.

        return [
            {
                id: 'mode', title: this.translate('FIELDS.MODE'), type: 'combo', onChange: async (_field: FormBuilderField, values: ModalValues) => this.handleModeChange(type, values), required: true, values: [
                    {
                        key: 'once', value: this.translate(`${key}.FIELDS.ONCE`), fields: [
                            type === 'time'
                                ? DateTimeField('trigger.value', this.translate(`${key}.FIELDS.TRIGGER_VALUE.ONCE`), true)
                                : TextField('trigger.value', this.translate(`${key}.FIELDS.TRIGGER_VALUE.ONCE`), true, null, null),
                        ],
                    }, {
                        key: 'repeating', value: this.translate(`${key}.FIELDS.REPEATING`, { unit }), fields: [
                            TextField('trigger.value', this.translate(`${key}.FIELDS.TRIGGER_VALUE.REPEATING`, { unit: unit && unit.replace(unit[0], unit[0].toUpperCase()) }), true, null, null),
                            ...(type === 'time' ? [
                                DateTimeField('trigger.start', this.translate(`FIELDS.STARTING_AT`), true),
                            ] : [
                                    {
                                        id: 'reset.type', title: this.translate('FIELDS.RESET'), type: 'combo', onChange: async (_field: FormBuilderField, values: ModalValues) => this.handleResetTypeChange(values), required: true, values: [
                                            {
                                                key: 'none', value: this.translate('FIELDS.NONE'), fields: [
                                                    { id: 'trigger.start', title: this.translate('FIELDS.STARTING_AT'), type: 'text', required: true, description: this.translate(`${key}.FIELDS.STARTING_AT_DESC`) },
                                                ],
                                            },
                                            {
                                                key: 'daily', value: this.translate('FIELDS.DAILY'), fields: [
                                                    NumberField('reset.daily.recurrence', this.translate('FIELDS.EVERY'), true, null, null, this.translate('FIELDS.DAYS')),
                                                    ComboField('reset.counter', this.translate('FIELDS.RESET_COUNTER'), true, resetCounter, {}),
                                                    DateTimeField('trigger.start', this.translate('FIELDS.STARTING_AT'), true),
                                                    ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), false, timeZones.map(x => ({ key: x.id, value: x.name })), {}),
                                                ],
                                            },
                                            {
                                                key: 'weekly', value: this.translate('FIELDS.WEEKLY'), fields: [
                                                    CheckListField('reset.weekly.days', this.translate('FIELDS.WEEKDAYS'), false, DAYS_OF_THE_WEEK.map((day, i) => ({ key: i.toString(), value: this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${day}`) })), {}),
                                                    ComboField('reset.counter', this.translate('FIELDS.RESET_COUNTER'), true, resetCounter, {}),
                                                ],
                                            },
                                            {
                                                key: 'monthly', value: this.translate('FIELDS.MONTLY'), fields: [
                                                    CheckListField('reset.monthly.months', this.translate('FIELDS.MONTHS'), false, MONTHS_OF_THE_YEAR.map((month, i) => ({ key: (++i).toString(), value: this.admin.i18n.instant(`SHARED.MONTHS.LONG.${month}`) })), {}), // key index starts at 1
                                                    ComboField('reset.monthly.day', this.translate('FIELDS.DAY'), true, [...Array(31).keys()].map(n => ({ key: (++n).toString(), value: n })), {}), // array from 1...31 of type FormBuilderKeyValue
                                                    ComboField('reset.counter', this.translate('FIELDS.RESET_COUNTER'), true, resetCounter, {}),
                                                ],
                                            },
                                        ],
                                    },
                                ]),
                        ],
                    },
                ],
            },
        ];
    }

    handleResetTypeChange(values: ModalValues) {
        const triggerStart = get(values, 'trigger.start');

        if (values.reset.type === 'none') {
            set(values, 'trigger.start', triggerStart);
        } else {
            set(values, 'trigger.start', moment.utc(triggerStart, moment.ISO_8601, true).isValid() ? triggerStart : new Date().toISOString());
        }
    }

    private handleModeChange(type: ReminderType, values: ModalValues) {
        if (type === 'time') {
            const triggerValue = get(values, 'trigger.value');

            if (values.mode === 'once') {
                set(values, 'trigger.value', triggerValue && moment.utc(triggerValue, moment.ISO_8601, true).isValid() ? triggerValue : new Date().toISOString());
            }

            if (values.mode === 'repeating') {
                const triggerStart = get(values, 'trigger.start');
                set(values, 'trigger.start', moment.utc(triggerStart, moment.ISO_8601, true).isValid() ? triggerStart : new Date().toISOString());
                set(values, 'trigger.value', triggerValue && moment.utc(triggerValue, moment.ISO_8601, true).isValid() ? '1' : triggerValue);
            }
        }
    }
}
